import type { ContextAdjustment, MessageWarning } from '@/types/chat.types';

/**
 * Build a non-blocking notice describing how the request was reshaped to fit
 * the model's context window (history trimmed/summarized, retrieval context
 * capped). Returns undefined when nothing was adjusted. Surfaced so trimming
 * and summarization are never silent.
 */
export function buildContextAdjustmentWarning(
  adjustment: ContextAdjustment
): MessageWarning | undefined {
  const { trimmedMessages, contextTruncated, summarizedMessages } = adjustment;
  if (!trimmedMessages && !contextTruncated && !summarizedMessages) {
    return undefined;
  }

  const parts: string[] = [];
  if (summarizedMessages > 0) {
    parts.push(
      `${summarizedMessages} earlier message${
        summarizedMessages === 1 ? ' was' : 's were'
      } summarized`
    );
  }
  if (trimmedMessages > 0) {
    parts.push(
      `${trimmedMessages} earlier message${
        trimmedMessages === 1 ? ' was' : 's were'
      } removed`
    );
  }
  if (contextTruncated) {
    parts.push('some retrieval context was shortened');
  }

  const list =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

  return {
    type: 'context_adjusted',
    title:
      summarizedMessages > 0
        ? 'Older messages summarized to fit'
        : 'Conversation shortened to fit',
    message:
      `To stay within this model's context window, ${list}. Some earlier ` +
      'detail may be missing from this response.',
    actionable: ['Start a new conversation to retain full detail'],
    icon: 'AlertTriangle',
  };
}

/**
 * Combine two warnings into the single slot a message can display. The primary
 * warning's type/title/icon win; messages and actionable tips are concatenated
 * so neither notice is dropped. Either side may be undefined.
 */
export function mergeWarnings(
  primary?: MessageWarning,
  secondary?: MessageWarning
): MessageWarning | undefined {
  if (!primary) return secondary;
  if (!secondary) return primary;

  const details = [primary.details, secondary.details]
    .filter(Boolean)
    .join(' ');
  const actionable = [
    ...(primary.actionable ?? []),
    ...(secondary.actionable ?? []),
  ];

  return {
    type: primary.type,
    title: primary.title,
    message: [primary.message, secondary.message].filter(Boolean).join(' '),
    details: details || undefined,
    actionable: actionable.length > 0 ? actionable : undefined,
    icon: primary.icon,
  };
}
